const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const MAX_SDP_BYTES = 64 * 1024;
const DEFAULT_SIGNAL_TIMEOUT_MS = 10_000;
const DEFAULT_NEGOTIATION_TIMEOUT_MS = 30_000;
const DEFAULT_GATHER_TIMEOUT_MS = 12_000;
const DEFAULT_DISCONNECT_GRACE_MS = 8_000;
const MEDIA_EVENTS = [
  "media_state",
  "media_offer",
  "media_answer",
  "media_failed",
  "media_ended",
  "recording_state",
];
const CONTROL_ACTIONS = new Set([
  "hangup",
  "hold",
  "resume",
  "mute",
  "unmute",
  "dtmf",
]);

export const NATIVE_MEDIA_STATUS = Object.freeze({
  IDLE: "idle",
  WAITING: "waiting",
  ACQUIRING_MICROPHONE: "acquiring_microphone",
  GATHERING: "gathering",
  NEGOTIATING: "negotiating",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  FAILED: "failed",
  ENDED: "ended",
});

export const isUuid = (value) =>
  typeof value === "string" && UUID_PATTERN.test(value.trim());

const canonicalUuid = (value) =>
  isUuid(value) ? value.trim().toLowerCase() : null;

const errorMessage = (value, fallback) => {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value?.reason === "string" && value.reason.trim()) {
    return value.reason.trim();
  }
  return fallback;
};

const sdpBytes = (sdp) => {
  if (typeof TextEncoder === "function") {
    return new TextEncoder().encode(sdp).byteLength;
  }
  return sdp.length;
};

export const validateAudioSdp = (sdp) => {
  if (typeof sdp !== "string" || !sdp.trim()) {
    throw new Error("The media server returned an empty SDP document.");
  }
  if (sdpBytes(sdp) > MAX_SDP_BYTES) {
    throw new Error("The media server returned an oversized SDP document.");
  }

  const lines = sdp.split(/\r?\n/);
  const audioSections = lines.filter((line) => line.startsWith("m=audio "));
  const unsupportedSections = lines.filter(
    (line) => line.startsWith("m=") && !line.startsWith("m=audio "),
  );

  if (audioSections.length !== 1 || unsupportedSections.length > 0) {
    throw new Error(
      "Native calls require exactly one audio-only media section.",
    );
  }
  if (!lines.some((line) => line.startsWith("a=ice-ufrag:"))) {
    throw new Error("The SDP document is missing its ICE username fragment.");
  }
  if (!lines.some((line) => line.startsWith("a=ice-pwd:"))) {
    throw new Error("The SDP document is missing its ICE password.");
  }
  if (!lines.some((line) => /^a=fingerprint:sha-256\s+/i.test(line))) {
    throw new Error("The SDP document is missing a SHA-256 DTLS fingerprint.");
  }
  if (!lines.some((line) => line === "a=rtcp-mux")) {
    throw new Error("The SDP document does not require RTCP multiplexing.");
  }

  return sdp;
};

const normalizeIceUrl = (url) => {
  if (typeof url !== "string") {
    throw new Error("An ICE server URL is not a string.");
  }

  const normalized = url.trim();
  if (!/^(stun|turn|turns):/i.test(normalized)) {
    throw new Error("Only STUN and TURN ICE server URLs are supported.");
  }
  if (/stun\d*\.l\.google\.com/i.test(normalized)) {
    throw new Error("Public Google STUN is not permitted for native calls.");
  }
  return normalized;
};

export const normalizeIceServers = (servers) => {
  if (!Array.isArray(servers) || servers.length === 0) {
    throw new Error(
      "Native media has no owner-authenticated self-hosted ICE/TURN configuration.",
    );
  }

  return servers.map((server) => {
    if (!server || typeof server !== "object") {
      throw new Error("The media server returned an invalid ICE server entry.");
    }

    const sourceUrls = Array.isArray(server.urls) ? server.urls : [server.urls];
    if (sourceUrls.length === 0) {
      throw new Error("The media server returned an ICE server without a URL.");
    }
    const urls = sourceUrls.map(normalizeIceUrl);
    const hasTurn = urls.some((url) => /^turns?:/i.test(url));

    if (
      hasTurn &&
      (typeof server.username !== "string" ||
        !server.username.trim() ||
        typeof server.credential !== "string" ||
        !server.credential)
    ) {
      throw new Error("TURN requires an ephemeral username and credential.");
    }

    return {
      urls: Array.isArray(server.urls) ? urls : urls[0],
      ...(hasTurn
        ? {
            username: server.username,
            credential: server.credential,
          }
        : {}),
    };
  });
};

const receiveReply = (push, timeoutMs, fallback) =>
  new Promise((resolve, reject) => {
    let settled = false;
    let timeout;
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      callback(value);
    };

    timeout = setTimeout(
      () => finish(reject, new Error(`${fallback} timed out.`)),
      timeoutMs,
    );

    push
      .receive("ok", (reply) => finish(resolve, reply || {}))
      .receive("error", (reply) =>
        finish(reject, new Error(errorMessage(reply, `${fallback} failed.`))),
      )
      .receive("timeout", () =>
        finish(reject, new Error(`${fallback} timed out.`)),
      );
  });

export class NativeMediaSession {
  constructor({
    socket,
    callId,
    ownerId,
    audioElement,
    onState = () => {},
    onRejectedSignal = () => {},
    getUserMedia,
    peerConnectionFactory,
    mediaStreamFactory,
    signalTimeoutMs = DEFAULT_SIGNAL_TIMEOUT_MS,
    negotiationTimeoutMs = DEFAULT_NEGOTIATION_TIMEOUT_MS,
    gatherTimeoutMs = DEFAULT_GATHER_TIMEOUT_MS,
    disconnectGraceMs = DEFAULT_DISCONNECT_GRACE_MS,
  }) {
    this.socket = socket;
    this.callId = canonicalUuid(callId);
    this.ownerId = canonicalUuid(ownerId);
    this.audioElement = audioElement;
    this.onState = onState;
    this.onRejectedSignal = onRejectedSignal;
    this.getUserMedia =
      getUserMedia ||
      ((constraints) => navigator.mediaDevices.getUserMedia(constraints));
    this.peerConnectionFactory =
      peerConnectionFactory || ((config) => new RTCPeerConnection(config));
    this.mediaStreamFactory =
      mediaStreamFactory || ((tracks) => new MediaStream(tracks));
    this.signalTimeoutMs = signalTimeoutMs;
    this.negotiationTimeoutMs = negotiationTimeoutMs;
    this.gatherTimeoutMs = gatherTimeoutMs;
    this.disconnectGraceMs = disconnectGraceMs;

    this.channel = null;
    this.channelRefs = [];
    this.peerConnection = null;
    this.localStream = null;
    this.remoteStream = null;
    this.iceServers = null;
    this.iceTransportPolicy = "relay";
    this.negotiationId = null;
    this.generation = null;
    this.browserRole = null;
    this.expected = null;
    this.predictive = false;
    this.attachmentPromise = null;
    this.attachmentConfirmed = false;
    this.remoteOffer = null;
    this.remoteAnswerPending = false;
    this.playbackReady = false;
    this.muted = false;
    this.held = false;
    this.connectPromise = null;
    this.operationEpoch = 0;
    this.deadlineTimer = null;
    this.disconnectTimer = null;
    this.terminated = false;
    this.snapshot = {
      status: NATIVE_MEDIA_STATUS.IDLE,
      callId: this.callId,
      ownerId: this.ownerId,
      negotiationId: null,
      generation: null,
      direction: null,
      browserRole: null,
      expected: null,
      deadlineAt: null,
      error: null,
      capabilities: null,
      recording: false,
      recordingStatus: "stopped",
      recordingPending: false,
      recordingUrl: null,
      transcription: null,
      recordingError: null,
      predictiveAgentConnected: null,
    };

    if (!this.callId || !this.ownerId) {
      throw new Error("Native media requires canonical call and owner UUIDs.");
    }
    if (!this.socket || typeof this.socket.channel !== "function") {
      throw new Error(
        "Native media requires the authenticated application socket.",
      );
    }
  }

  get state() {
    return this.snapshot;
  }

  get isConnected() {
    return this.snapshot.status === NATIVE_MEDIA_STATUS.CONNECTED;
  }

  updateState(status, fields = {}) {
    this.snapshot = {
      ...this.snapshot,
      ...fields,
      status,
      callId: this.callId,
      ownerId: this.ownerId,
      negotiationId: this.negotiationId,
      generation: this.generation,
      browserRole: this.browserRole,
      expected: this.expected,
    };
    this.onState(this.snapshot);
    return this.snapshot;
  }

  async join() {
    if (this.terminated) {
      throw new Error("This native media session has ended.");
    }
    if (this.channel) return this.snapshot;

    const channel = this.socket.channel(`call:${this.callId}`, {});
    this.channel = channel;

    const handlers = {
      media_state: (payload) => this.handleMediaState(payload),
      media_offer: (payload) => this.handleMediaOffer(payload),
      media_answer: (payload) => this.handleMediaAnswer(payload),
      media_failed: (payload) => this.handleMediaFailure(payload),
      media_ended: (payload) => this.handleMediaEnded(payload),
      recording_state: (payload) => this.handleRecordingState(payload),
    };

    MEDIA_EVENTS.forEach((event) => {
      const ref = channel.on(event, handlers[event]);
      this.channelRefs.push([event, ref]);
    });

    if (typeof channel.onError === "function") {
      channel.onError(() => {
        if (
          !this.terminated &&
          this.snapshot.status !== NATIVE_MEDIA_STATUS.CONNECTED
        ) {
          this.updateState(NATIVE_MEDIA_STATUS.WAITING, {
            error: "Media signaling is reconnecting.",
          });
        }
      });
    }

    return new Promise((resolve, reject) => {
      let initialSettled = false;
      const timeout = setTimeout(() => {
        if (initialSettled) return;
        initialSettled = true;
        const error = new Error(
          "Joining the owned call media channel timed out.",
        );
        this.fail(error.message);
        reject(error);
      }, this.signalTimeoutMs);

      const settleInitial = (callback, value) => {
        if (initialSettled) return;
        initialSettled = true;
        clearTimeout(timeout);
        callback(value);
      };

      channel
        .join(this.signalTimeoutMs)
        .receive("ok", (reply) => {
          Promise.resolve(this.applyAuthoritativeJoin(reply))
            .then(() => settleInitial(resolve, this.snapshot))
            .catch((error) => {
              this.fail(
                errorMessage(error, "Could not join the call media channel."),
              );
              settleInitial(reject, error);
            });
        })
        .receive("error", (reply) => {
          const error = new Error(
            errorMessage(reply, "Could not join the call media channel."),
          );
          if (initialSettled) {
            if (this.snapshot.status !== NATIVE_MEDIA_STATUS.CONNECTED) {
              this.updateState(NATIVE_MEDIA_STATUS.WAITING, {
                error: error.message,
              });
            }
            return;
          }
          this.fail(error.message);
          settleInitial(reject, error);
        })
        .receive("timeout", () => {
          if (initialSettled) return;
          const error = new Error(
            "Joining the owned call media channel timed out.",
          );
          this.fail(error.message);
          settleInitial(reject, error);
        });
    });
  }

  async applyAuthoritativeJoin(reply) {
    const callId = canonicalUuid(reply?.call_id);
    const ownerId = canonicalUuid(reply?.owner_id);
    const media = reply?.media;

    if (callId !== this.callId || ownerId !== this.ownerId || !media) {
      throw new Error(
        "The call channel returned foreign or incomplete media state.",
      );
    }

    const negotiationId = canonicalUuid(media.negotiation_id);
    const generation = media.generation;
    const browserRole = media.browser_role;
    const expected = media.expected;

    if (
      !negotiationId ||
      !Number.isInteger(generation) ||
      generation < 0 ||
      !["inbound", "outbound"].includes(reply.direction) ||
      ![
        "awaiting_media",
        "connecting",
        "connected",
        "failed",
        "ended",
      ].includes(media.status) ||
      !["offerer", "answerer"].includes(browserRole) ||
      (reply.direction === "outbound" &&
        browserRole !== (media.dialing_mode === "predictive" ? "answerer" : "offerer")) ||
      (reply.direction === "inbound" && browserRole !== "answerer") ||
      !["offer", "answer", "none"].includes(expected)
    ) {
      throw new Error(
        "The call channel returned invalid canonical media state.",
      );
    }

    const capabilities = reply.capabilities;
    if (
      !capabilities ||
      ["recording", "transcription", "hold", "mute", "dtmf", "transfer"].some(
        (capability) => typeof capabilities[capability] !== "boolean",
      )
    ) {
      throw new Error("The call channel returned invalid capability state.");
    }
    this.snapshot.capabilities = { ...capabilities };

    const replacingNegotiation =
      this.negotiationId !== null &&
      (this.negotiationId !== negotiationId || this.generation !== generation);
    if (replacingNegotiation) this.disposePeer();

    this.negotiationId = negotiationId;
    this.generation = generation;
    this.browserRole = browserRole;
    this.expected = expected;
    this.predictive = media.dialing_mode === "predictive";
    if (replacingNegotiation) {
      this.attachmentConfirmed = false;
      this.attachmentPromise = null;
    }
    this.snapshot.direction = reply.direction;
    if (
      reply.ice_transport_policy !== undefined &&
      !["all", "relay"].includes(reply.ice_transport_policy)
    ) {
      throw new Error(
        "The call channel returned an invalid ICE transport policy.",
      );
    }
    this.iceTransportPolicy = reply.ice_transport_policy || "relay";

    try {
      this.iceServers = normalizeIceServers(reply.ice_servers);
    } catch (error) {
      this.iceServers = null;
      this.updateState(NATIVE_MEDIA_STATUS.WAITING, {
        deadlineAt: media.deadline_at || null,
        error: error.message,
      });
    }

    this.armDeadline(media.deadline_at);
    if (this.terminated) return;

    if (media.status === "failed") {
      this.fail("The media server reported negotiation failure.");
      return;
    }
    if (media.status === "ended") {
      this.endFromServer();
      return;
    }

    if (media.sdp) {
      const finalOffer =
        browserRole === "answerer" &&
        media.sdp_type === "offer" &&
        media.provisional === false;
      const finalAnswer =
        browserRole === "offerer" &&
        media.sdp_type === "answer" &&
        media.provisional === false;

      if (!finalOffer && !finalAnswer) {
        throw new Error(
          "The media state contains SDP for the wrong browser role.",
        );
      }
      const validatedSdp = validateAudioSdp(media.sdp);

      if (finalOffer && expected === "answer") {
        this.remoteOffer = validatedSdp;
      } else if (
        finalOffer &&
        expected === "none" &&
        this.peerConnection?.remoteDescription?.type === "offer"
      ) {
        this.syncPeerConnectionState();
      } else if (
        finalAnswer &&
        (this.peerConnection?.remoteDescription?.type === "answer" ||
          this.remoteAnswerPending)
      ) {
        this.expected = "none";
        this.syncPeerConnectionState();
      } else if (finalAnswer) {
        await this.applyRemoteAnswer(validatedSdp);
      } else {
        throw new Error(
          "The media SDP cannot resume the current browser state.",
        );
      }
    }

    if (!this.peerConnection) {
      this.updateState(NATIVE_MEDIA_STATUS.WAITING, {
        deadlineAt: media.deadline_at || null,
        error: this.iceServers
          ? null
          : this.snapshot.error || "Self-hosted ICE/TURN is unavailable.",
      });
    } else if (media.status === "connecting" || media.status === "connected") {
      this.updateState(NATIVE_MEDIA_STATUS.CONNECTING, { error: null });
      this.syncPeerConnectionState();
    }

    if (capabilities.recording) {
      try {
        await this.recordingStatus();
      } catch (error) {
        this.updateState(this.snapshot.status, {
          recordingError:
            error.message || "Could not retrieve native recording state.",
        });
      }
    }
  }

  matchesGeneration(payload, requireOwner = false) {
    const matches =
      canonicalUuid(payload?.call_id) === this.callId &&
      canonicalUuid(payload?.negotiation_id) === this.negotiationId &&
      payload?.generation === this.generation &&
      (!requireOwner || canonicalUuid(payload?.owner_id) === this.ownerId);

    if (!matches) this.onRejectedSignal("foreign_or_stale_generation");
    return matches;
  }

  handleMediaState(payload) {
    if (!this.matchesGeneration(payload, true)) return;
    if (
      ![
        "awaiting_media",
        "connecting",
        "connected",
        "failed",
        "ended",
      ].includes(payload.status) ||
      !["offer", "answer", "none"].includes(payload.expected)
    ) {
      this.onRejectedSignal("invalid_media_state");
      return;
    }

    this.expected = payload.expected;
    this.armDeadline(payload.deadline_at);
    if (this.terminated) return;

    if (payload.status === "failed") {
      this.fail("The media server reported negotiation failure.");
    } else if (payload.status === "ended") {
      this.endFromServer();
    } else if (!this.peerConnection) {
      this.updateState(NATIVE_MEDIA_STATUS.WAITING, {
        deadlineAt: payload.deadline_at || null,
        error: this.snapshot.error,
      });
    } else if (
      payload.status === "connecting" ||
      payload.status === "connected"
    ) {
      this.updateState(NATIVE_MEDIA_STATUS.CONNECTING, { error: null });
      this.syncPeerConnectionState();
    } else {
      this.updateState(NATIVE_MEDIA_STATUS.NEGOTIATING, { error: null });
    }
  }

  handleMediaOffer(payload) {
    if (!this.matchesGeneration(payload, true)) return;
    if (payload.sdp_type !== "offer" || payload.provisional !== false) {
      this.onRejectedSignal("invalid_offer_envelope");
      return;
    }
    if (this.browserRole !== "answerer" || this.expected !== "answer") {
      this.onRejectedSignal("unexpected_offer");
      return;
    }

    try {
      this.remoteOffer = validateAudioSdp(payload.sdp);
      this.updateState(NATIVE_MEDIA_STATUS.WAITING, { error: null });
    } catch (error) {
      this.fail(error.message);
    }
  }

  handleMediaAnswer(payload) {
    if (!this.matchesGeneration(payload, true)) return;
    if (
      payload.sdp_type !== "answer" ||
      typeof payload.provisional !== "boolean"
    ) {
      this.onRejectedSignal("invalid_answer_envelope");
      return;
    }
    if (this.browserRole !== "offerer") {
      this.onRejectedSignal("unexpected_answer");
      return;
    }
    if (
      !this.peerConnection?.localDescription ||
      this.peerConnection.remoteDescription ||
      this.remoteAnswerPending
    ) {
      this.onRejectedSignal("unexpected_answer");
      return;
    }
    if (payload.provisional) {
      this.updateState(NATIVE_MEDIA_STATUS.NEGOTIATING, {
        error: "Early media is unavailable; waiting for the final answer.",
      });
      return;
    }

    const epoch = this.operationEpoch;
    this.remoteAnswerPending = true;
    this.applyRemoteAnswer(payload.sdp)
      .catch((error) => {
        if (epoch === this.operationEpoch && !this.terminated) {
          this.fail(error.message);
        }
      })
      .finally(() => {
        if (epoch === this.operationEpoch) this.remoteAnswerPending = false;
      });
  }
  handleMediaFailure(payload) {
    if (!this.matchesGeneration(payload, true)) return;
    this.fail(
      errorMessage(payload?.reason, "Native media negotiation failed."),
    );
  }

  handleMediaEnded(payload) {
    if (!this.matchesGeneration(payload, true)) return;
    if (typeof payload.predictive_agent_connected === "boolean") {
      this.snapshot.predictiveAgentConnected = payload.predictive_agent_connected;
    }
    this.endFromServer();
  }

  recordingFields(payload) {
    if (
      typeof payload?.recording !== "boolean" ||
      !["recording", "stopped"].includes(payload?.status) ||
      (payload.recording && payload.status !== "recording") ||
      (!payload.recording && payload.status !== "stopped") ||
      (payload.recording_url !== undefined &&
        payload.recording_url !== null &&
        typeof payload.recording_url !== "string") ||
      (payload.transcription !== undefined &&
        payload.transcription !== "queued")
    ) {
      throw new Error("The call channel returned invalid recording state.");
    }
    return {
      recording: payload.recording,
      recordingStatus: payload.status,
      ...(payload.recording_url !== undefined
        ? { recordingUrl: payload.recording_url }
        : {}),
      ...(payload.transcription !== undefined
        ? { transcription: payload.transcription }
        : {}),
      recordingError: null,
    };
  }

  handleRecordingState(payload) {
    if (!this.matchesGeneration(payload, true)) return;
    try {
      this.updateState(this.snapshot.status, this.recordingFields(payload));
    } catch (_) {
      this.onRejectedSignal("invalid_recording_state");
    }
  }

  async recordingCommand(command) {
    if (!this.snapshot.capabilities?.recording) {
      throw new Error("Recording is not enabled for this native call.");
    }
    if (!this.channel || this.terminated) {
      throw new Error("The native call channel is not available.");
    }
    if (this.snapshot.recordingPending) {
      throw new Error("A native recording operation is already in progress.");
    }

    this.updateState(this.snapshot.status, {
      recordingPending: true,
      recordingError: null,
    });
    try {
      const reply = await this.push(`call:recording_${command}`, {});
      this.assertControlReply(reply);
      if (
        command === "stop" &&
        (!reply.recording_url || reply.transcription !== "queued")
      ) {
        throw new Error(
          "Native recording did not return a durable recording and transcription job.",
        );
      }
      const fields = this.recordingFields(reply);
      this.updateState(this.snapshot.status, {
        ...fields,
        recordingPending: false,
      });
      return reply;
    } catch (error) {
      if (!this.terminated) {
        this.updateState(this.snapshot.status, {
          recordingPending: false,
          recordingError: error.message || "Native recording failed.",
        });
      }
      throw error;
    }
  }

  startRecording() {
    return this.recordingCommand("start");
  }

  stopRecording() {
    return this.recordingCommand("stop");
  }

  recordingStatus() {
    return this.recordingCommand("status");
  }

  async connectAudio() {
    if (this.terminated) {
      throw new Error("This native media session has ended.");
    }
    if (!this.channel) await this.join();

    if (
      this.peerConnection?.connectionState === "connected" &&
      this.remoteStream
    ) {
      await this.playRemoteAudio();
      this.syncPeerConnectionState();
      return this.isConnected;
    }
    if (this.connectPromise) return this.connectPromise;
    if (!this.iceServers) {
      const error = new Error(
        this.snapshot.error ||
          "Native media cannot connect without self-hosted ICE/TURN.",
      );
      this.fail(error.message);
      throw error;
    }
    const serverExpectsBrowserSdp =
      (this.browserRole === "offerer" && this.expected === "offer") ||
      (this.browserRole === "answerer" &&
        this.expected === "answer" &&
        Boolean(this.remoteOffer));
    if (!serverExpectsBrowserSdp) {
      const error = new Error(
        "The server is not ready for browser media negotiation.",
      );
      this.updateState(NATIVE_MEDIA_STATUS.WAITING, { error: error.message });
      throw error;
    }

    this.connectPromise = this.startNegotiation();
    try {
      return await this.connectPromise;
    } finally {
      this.connectPromise = null;
    }
  }

  async startNegotiation() {
    const epoch = ++this.operationEpoch;
    this.updateState(NATIVE_MEDIA_STATUS.ACQUIRING_MICROPHONE, { error: null });

    let stream;
    try {
      stream = await this.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
    } catch (error) {
      const reason =
        error?.name === "NotAllowedError"
          ? "Microphone permission was denied."
          : "The microphone is unavailable.";
      this.fail(reason);
      throw new Error(reason);
    }

    if (epoch !== this.operationEpoch || this.terminated) {
      stream.getTracks().forEach((track) => track.stop());
      throw new Error("The media negotiation is no longer current.");
    }

    const audioTracks = stream.getAudioTracks();
    stream
      .getTracks()
      .filter((track) => track.kind !== "audio")
      .forEach((track) => track.stop());
    if (audioTracks.length === 0) {
      stream.getTracks().forEach((track) => track.stop());
      this.fail("The selected input device did not provide an audio track.");
      throw new Error(
        "The selected input device did not provide an audio track.",
      );
    }

    this.localStream = stream;
    const pc = this.peerConnectionFactory({
      iceServers: this.iceServers,
      iceTransportPolicy: this.iceTransportPolicy,
      // Single-audio SIP offers may omit BUNDLE; still offer it when we initiate.
      bundlePolicy: "balanced",
      rtcpMuxPolicy: "require",
    });
    this.peerConnection = pc;
    this.installPeerHandlers(pc, epoch);
    audioTracks.forEach((track) => pc.addTrack(track, stream));
    this.applyLocalTrackState();
    this.armNegotiationTimeout();

    try {
      if (this.browserRole === "offerer" && this.expected === "offer") {
        await this.sendBrowserOffer(pc, epoch);
      } else if (
        this.browserRole === "answerer" &&
        this.expected === "answer" &&
        this.remoteOffer
      ) {
        await this.sendBrowserAnswer(pc, epoch);
      } else {
        throw new Error(
          "The server is not expecting browser SDP for this call.",
        );
      }
      return true;
    } catch (error) {
      if (epoch === this.operationEpoch && !this.terminated) {
        this.fail(error.message);
      }
      throw error;
    }
  }

  installPeerHandlers(pc, epoch) {
    pc.ontrack = (event) => {
      if (epoch !== this.operationEpoch || event.track?.kind !== "audio") {
        event.track?.stop?.();
        return;
      }

      this.remoteStream =
        event.streams?.[0] || this.mediaStreamFactory([event.track]);
      this.playRemoteAudio().finally(() => this.syncPeerConnectionState());
    };
    pc.onconnectionstatechange = () => this.syncPeerConnectionState();
    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === "failed") {
        this.fail("ICE connectivity failed for the native call.");
      }
    };
  }

  assertCurrentPeer(pc, epoch) {
    if (
      this.terminated ||
      this.operationEpoch !== epoch ||
      this.peerConnection !== pc
    ) {
      throw new Error("The media negotiation is no longer current.");
    }
  }

  async sendBrowserOffer(pc, epoch) {
    const negotiationId = this.negotiationId;
    const offer = await pc.createOffer({ offerToReceiveAudio: true });
    this.assertCurrentPeer(pc, epoch);
    await pc.setLocalDescription(offer);
    const sdp = await this.waitForIceGathering(pc, epoch);
    this.assertCurrentPeer(pc, epoch);
    validateAudioSdp(sdp);
    this.updateState(NATIVE_MEDIA_STATUS.NEGOTIATING, { error: null });

    const reply = await this.push("media_offer", {
      negotiation_id: negotiationId,
      sdp,
    });
    this.assertCurrentPeer(pc, epoch);
    this.assertControlReply(reply);
    if (pc.remoteDescription) {
      this.expected = "none";
      this.syncPeerConnectionState();
    } else {
      this.expected = "answer";
      this.updateState(NATIVE_MEDIA_STATUS.NEGOTIATING, { error: null });
    }
  }

  async sendBrowserAnswer(pc, epoch) {
    const negotiationId = this.negotiationId;
    validateAudioSdp(this.remoteOffer);
    await pc.setRemoteDescription({ type: "offer", sdp: this.remoteOffer });
    this.assertCurrentPeer(pc, epoch);
    const answer = await pc.createAnswer();
    this.assertCurrentPeer(pc, epoch);
    await pc.setLocalDescription(answer);
    const sdp = await this.waitForIceGathering(pc, epoch);
    this.assertCurrentPeer(pc, epoch);
    validateAudioSdp(sdp);
    this.updateState(NATIVE_MEDIA_STATUS.NEGOTIATING, { error: null });

    const reply = await this.push("media_answer", {
      negotiation_id: negotiationId,
      sdp,
    });
    this.assertCurrentPeer(pc, epoch);
    this.assertControlReply(reply);
    this.expected = "none";
    this.updateState(NATIVE_MEDIA_STATUS.CONNECTING, { error: null });
    this.syncPeerConnectionState();
  }
  async applyRemoteAnswer(sdp) {
    validateAudioSdp(sdp);
    const pc = this.peerConnection;
    if (!pc || !pc.localDescription || this.browserRole !== "offerer") {
      throw new Error(
        "The final media answer does not match a live browser offer generation.",
      );
    }
    if (pc.remoteDescription) return;

    await pc.setRemoteDescription({ type: "answer", sdp });
    if (pc !== this.peerConnection || this.terminated) return;
    this.expected = "none";
    this.updateState(NATIVE_MEDIA_STATUS.CONNECTING, { error: null });
    this.syncPeerConnectionState();
  }

  waitForIceGathering(pc, epoch) {
    this.assertCurrentPeer(pc, epoch);
    if (pc.iceGatheringState === "complete") {
      return Promise.resolve(pc.localDescription?.sdp || "");
    }

    this.updateState(NATIVE_MEDIA_STATUS.GATHERING, { error: null });
    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = (callback, value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        pc.removeEventListener?.("icegatheringstatechange", onStateChange);
        callback(value);
      };
      const onStateChange = () => {
        if (epoch !== this.operationEpoch) {
          finish(
            reject,
            new Error("The media negotiation is no longer current."),
          );
        } else if (pc.iceGatheringState === "complete") {
          finish(resolve, pc.localDescription?.sdp || "");
        }
      };
      const timeout = setTimeout(
        () =>
          finish(reject, new Error("Full ICE candidate gathering timed out.")),
        this.gatherTimeoutMs,
      );
      pc.addEventListener("icegatheringstatechange", onStateChange);
      onStateChange();
    });
  }

  setAudioElement(element) {
    if (this.audioElement === element) return;
    if (this.audioElement?.srcObject === this.remoteStream) {
      this.audioElement.srcObject = null;
    }
    this.audioElement = element;
    this.playbackReady = false;
    if (element && this.remoteStream && !this.terminated) {
      this.playRemoteAudio().then(() => this.syncPeerConnectionState());
    }
  }

  async playRemoteAudio() {
    const audioElement = this.audioElement;
    const remoteStream = this.remoteStream;
    if (!audioElement || !remoteStream) return false;

    audioElement.autoplay = true;
    audioElement.playsInline = true;
    if (audioElement.srcObject !== remoteStream) {
      audioElement.srcObject = remoteStream;
    }

    try {
      await audioElement.play();
      if (
        this.terminated ||
        this.audioElement !== audioElement ||
        this.remoteStream !== remoteStream
      ) {
        if (audioElement.srcObject === remoteStream) {
          audioElement.srcObject = null;
        }
        return false;
      }
      this.playbackReady = true;
      return true;
    } catch (_) {
      if (
        !this.terminated &&
        this.audioElement === audioElement &&
        this.remoteStream === remoteStream
      ) {
        this.playbackReady = false;
        this.updateState(NATIVE_MEDIA_STATUS.WAITING, {
          error: "Audio playback is blocked. Choose Connect Audio again.",
        });
      }
      return false;
    }
  }

  confirmPredictiveAttachment() {
    if (!this.predictive || this.attachmentConfirmed || this.attachmentPromise ||
        this.expected !== "none") return;
    const epoch = this.operationEpoch;
    const pc = this.peerConnection;
    const negotiationId = this.negotiationId;
    this.attachmentPromise = (async () => {
      // SDP/ACK completion is not media attachment. The server independently
      // requires encrypted browser-tag RTP before recording agent connection.
      for (let attempt = 0; attempt < 40; attempt += 1) {
        if (this.terminated || epoch !== this.operationEpoch ||
            this.peerConnection?.connectionState !== "connected") return;
        try {
          const reply = await this.push("media_connected", { negotiation_id: negotiationId });
          this.assertCurrentPeer(pc, epoch);
          this.assertControlReply(reply);
          if (this.negotiationId !== negotiationId ||
              canonicalUuid(reply?.negotiation_id) !== negotiationId ||
              reply?.status !== "connected" ||
              reply?.predictive_agent_connected !== true) {
            throw new Error("The media server did not confirm this predictive attachment.");
          }
          this.attachmentConfirmed = true;
          this.snapshot.predictiveAgentConnected = true;
          this.syncPeerConnectionState();
          return;
        } catch (error) {
          if (!String(error?.message || "").includes("media_not_flowing")) throw error;
          await new Promise((resolve) => setTimeout(resolve, 250));
        }
      }
      throw new Error("The relay did not confirm native predictive media attachment.");
    })().catch((error) => {
      if (!this.terminated && epoch === this.operationEpoch) this.fail(error.message);
    }).finally(() => {
      if (epoch === this.operationEpoch) this.attachmentPromise = null;
    });
  }

  syncPeerConnectionState() {
    const state = this.peerConnection?.connectionState;
    if (state === "connected") {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = null;

      if (this.remoteStream && this.playbackReady) {
        if (this.predictive && !this.attachmentConfirmed) {
          this.updateState(NATIVE_MEDIA_STATUS.CONNECTING, { error: null });
          this.confirmPredictiveAttachment();
          return;
        }
        clearTimeout(this.deadlineTimer);
        this.deadlineTimer = null;
        this.updateState(NATIVE_MEDIA_STATUS.CONNECTED, { error: null });
      } else {
        this.updateState(NATIVE_MEDIA_STATUS.CONNECTING, {
          error:
            "Media transport connected; waiting for playable remote audio.",
        });
      }
    } else if (state === "failed") {
      this.fail("The browser media transport failed.");
    } else if (state === "disconnected") {
      this.updateState(NATIVE_MEDIA_STATUS.CONNECTING, {
        error: "Media transport disconnected; attempting recovery.",
      });
      if (!this.disconnectTimer) {
        this.disconnectTimer = setTimeout(
          () => this.fail("The browser media transport did not recover."),
          this.disconnectGraceMs,
        );
      }
    } else if (state === "connecting" || state === "new") {
      this.updateState(NATIVE_MEDIA_STATUS.CONNECTING, { error: null });
    }
  }

  async control(action, payload = {}) {
    if (!CONTROL_ACTIONS.has(action)) {
      throw new Error("Unsupported native call control.");
    }
    if (!this.channel || this.terminated) {
      throw new Error("The native call media channel is not available.");
    }
    if (action === "dtmf" && !/^[0-9A-D*#]$/i.test(payload?.digit || "")) {
      throw new Error("Native DTMF requires exactly one valid digit.");
    }

    const reply = await this.push(`call:${action}`, payload);
    this.assertControlReply(reply);

    if (action === "mute") this.setMuted(true);
    if (action === "unmute") this.setMuted(false);
    if (action === "hold") this.setHeld(true);
    if (action === "resume") this.setHeld(false);
    return reply;
  }

  async hangup() {
    const reply = await this.control("hangup");
    if (String(reply?.status || "").toLowerCase() !== "completed") {
      throw new Error("The media server did not confirm that the call ended.");
    }
    if (typeof reply.predictive_agent_connected === "boolean") {
      this.snapshot.predictiveAgentConnected = reply.predictive_agent_connected;
    }
    this.close(NATIVE_MEDIA_STATUS.ENDED);
    return reply;
  }

  setMuted(muted) {
    this.muted = Boolean(muted);
    this.applyLocalTrackState();
    return this.muted;
  }

  setHeld(held) {
    this.held = Boolean(held);
    this.applyLocalTrackState();
    return this.held;
  }

  applyLocalTrackState() {
    const enabled = !this.muted && !this.held;
    this.localStream
      ?.getAudioTracks()
      .forEach((track) => (track.enabled = enabled));
  }

  getPeerConnection() {
    return this.peerConnection;
  }

  async push(event, payload) {
    if (!this.channel) {
      throw new Error("The native call media channel is not joined.");
    }
    return receiveReply(
      this.channel.push(event, payload, this.signalTimeoutMs),
      this.signalTimeoutMs,
      event,
    );
  }

  assertControlReply(reply) {
    if (canonicalUuid(reply?.call_id) !== this.callId) {
      throw new Error("The media server replied for a different call.");
    }
  }

  armDeadline(deadlineAt) {
    clearTimeout(this.deadlineTimer);
    this.deadlineTimer = null;
    this.snapshot.deadlineAt = deadlineAt || null;
    if (!deadlineAt) return;

    const delay = Date.parse(deadlineAt) - Date.now();
    if (!Number.isFinite(delay) || delay <= 0) {
      this.fail("The native media negotiation deadline expired.");
      return;
    }
    this.deadlineTimer = setTimeout(
      () => this.fail("The native media negotiation deadline expired."),
      Math.min(delay, 2_147_000_000),
    );
  }

  armNegotiationTimeout() {
    clearTimeout(this.deadlineTimer);
    const serverRemaining = this.snapshot.deadlineAt
      ? Date.parse(this.snapshot.deadlineAt) - Date.now()
      : Number.POSITIVE_INFINITY;
    const delay = Math.min(this.negotiationTimeoutMs, serverRemaining);
    if (!Number.isFinite(delay) && delay !== Number.POSITIVE_INFINITY) {
      this.fail("The native media negotiation deadline is invalid.");
      return;
    }
    if (delay <= 0) {
      this.fail("The native media negotiation deadline expired.");
      return;
    }
    this.deadlineTimer = setTimeout(
      () => this.fail("Native media negotiation timed out."),
      Math.min(delay, 2_147_000_000),
    );
  }

  fail(reason) {
    if (this.terminated) return;
    this.terminated = true;
    this.disposeAll();
    this.updateState(NATIVE_MEDIA_STATUS.FAILED, {
      error: errorMessage(reason, "Native media failed."),
      recording: false,
      recordingStatus: "stopped",
      recordingPending: false,
    });
  }

  endFromServer() {
    if (this.terminated) return;
    this.terminated = true;
    this.disposeAll();
    this.updateState(NATIVE_MEDIA_STATUS.ENDED, {
      error: null,
      recording: false,
      recordingStatus: "stopped",
      recordingPending: false,
    });
  }

  close(status = NATIVE_MEDIA_STATUS.IDLE) {
    if (!this.terminated) this.terminated = true;
    this.disposeAll();
    this.updateState(status, {
      error: null,
      recording: false,
      recordingStatus: "stopped",
      recordingPending: false,
    });
  }

  disposeAll() {
    this.disposePeer();
    clearTimeout(this.deadlineTimer);
    clearTimeout(this.disconnectTimer);
    this.deadlineTimer = null;
    this.disconnectTimer = null;

    if (this.channel) {
      this.channelRefs.forEach(([event, ref]) => this.channel.off(event, ref));
      this.channelRefs = [];
      this.channel.leave();
      this.channel = null;
    }
  }

  disposePeer() {
    this.operationEpoch += 1;
    this.remoteAnswerPending = false;
    if (this.peerConnection) {
      this.peerConnection.ontrack = null;
      this.peerConnection.onconnectionstatechange = null;
      this.peerConnection.oniceconnectionstatechange = null;
      this.peerConnection.close();
      this.peerConnection = null;
    }
    this.localStream?.getTracks().forEach((track) => track.stop());
    this.remoteStream?.getTracks?.().forEach((track) => track.stop());
    this.localStream = null;
    this.playbackReady = false;
    this.remoteStream = null;
    if (this.audioElement) this.audioElement.srcObject = null;
  }
}
